import { HStack, Link, Skeleton, Text, VStack } from '@chakra-ui/react';
import React from 'react';
import {
  Link as ReactRouterLink,
  Navigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';

import { AppError } from '../../../../../framework/models/errors';
import { ListUtils } from '../../../../../framework/utils/ListUtils';
import { TypeUtils } from '../../../../../framework/utils/UtilityTypes';
import DateMatter from '../../../../components/DateMatter';
import { AddButton, BackButton } from '../../../../components/StyledButton';
import StyledList from '../../../../components/StyledList';
import { pageKeywords, pageNames, pagePaths } from '../../../../constants/Page';
import { useGlobalStateMachine } from '../../../../GlobalStore';
import { useEngeneNavigate } from '../../../../hooks/navigate';
import { usePostExpenseSearch } from '../../../../hooks/queries/expense';
import useToastHook from '../../../../hooks/ToastHook';
import { MasterKey } from '../../../../models/gen/masterKey';
import { Formatter } from '../../../../utils/FormatUtils';
import { useMasterContext } from '../../../MasterProvider';
import { useSizeContext } from '../../../SizeProvider';
import Wireframe from '../../../wireframe/Wireframe';

/**
 * 現場経費画面 パラメータ
 */
export type MatterExpenseParams = {
  /**
   * 日毎現場ID
   */
  dailyMatterId: string;
};

/**
 * 現場経費画面
 * @returns
 */
const MatterExpense: React.FunctionComponent = () => {
  const { actions, state } = useGlobalStateMachine();
  if (TypeUtils.isNil(state.userInfo)) {
    return <Navigate to={pagePaths.ROOT} />;
  }

  const master = useMasterContext();
  const sizes = useSizeContext();
  const navigate = useEngeneNavigate();
  const { toast } = useToastHook();

  const [searchParams] = useSearchParams();
  const dateString = searchParams.get('date') || undefined;
  const [date, setDate] = React.useState(
    TypeUtils.isNilString(dateString)
      ? undefined
      : Formatter.createDate(dateString)
  );

  const { dailyMatterId } = useParams<MatterExpenseParams>();
  if (TypeUtils.isNilNumber(dailyMatterId)) {
    navigate(-1);
    return <></>;
  }
  const { data, error, isLoading, isError, refetch } = usePostExpenseSearch({
    mode: 3,
    dailyMatterId: parseInt(dailyMatterId),
    // yearMonth: dateString,
    userCodeWorker: state.userInfo.user_code,
    // applyUnitId: state.userInfo?.roleOptions?.isOutsourcer
    //   ? undefined
    //   : TypeUtils.isNil(date)
    //   ? undefined
    //   : date.getDate() < 16
    //   ? master.applyUnit.expense1?.id
    //   : master.applyUnit.expense2?.id,
  });
  React.useState(() => refetch());
  React.useEffect(() => {
    if (!isLoading) {
      const dailyMatterSummaries = data?.reference?.dailyMatterSummaries;
      if (ListUtils.isNotEmpty(dailyMatterSummaries)) {
        const d = dailyMatterSummaries[0]!.date;
        if (!TypeUtils.isNilString(d)) {
          setDate(Formatter.createDate(d));
        }
      }
    }
  }, [isLoading]);
  React.useEffect(() => {
    if (isError) {
      toast(error instanceof AppError ? error : '現場経費の検索に失敗しました');
    }
  }, [isError]);

  return (
    <Wireframe title={pageNames.MATTER_EXPENSE}>
      <VStack
        alignItems="start"
        fontSize={sizes.fontSize.rawMessage}
        width={sizes.width.styledMessageList}
      >
        <DateMatter
          customerName={
            ListUtils.isNotEmpty(data?.reference?.matterSummaries) &&
            !TypeUtils.isNilString(
              data?.reference?.matterSummaries[0].customerName
            )
              ? data?.reference?.matterSummaries[0].customerName
              : undefined
          }
          date={date}
          matterId={
            ListUtils.isNotEmpty(data?.reference?.matterSummaries) &&
            !TypeUtils.isNilString(
              data?.reference?.matterSummaries[0].matterName
            )
              ? data?.reference?.matterSummaries[0].matterId
              : undefined
          }
          matterName={
            ListUtils.isNotEmpty(data?.reference?.matterSummaries) &&
            !TypeUtils.isNilString(
              data?.reference?.matterSummaries[0].matterName
            )
              ? data?.reference?.matterSummaries[0].matterName
              : undefined
          }
        />
        <Skeleton isLoaded={!isLoading} width="100%">
          <StyledList
            hasBorder
            items={
              data?.results
                ?.filter(
                  (expense) =>
                    expense.userCodeWorker === state.userInfo?.user_code
                )
                .map((expense) => (
                  <HStack
                    justifyContent="space-between"
                    key={expense.expenseId}
                    minHeight="55px"
                  >
                    <Link
                      as={ReactRouterLink}
                      color="blue.500"
                      fontWeight="semibold"
                      onClick={() =>
                        actions.updatePageInfo({
                          MATTER_EXPENSE: {
                            matterSummary: ListUtils.isNotEmpty(
                              data?.reference?.matterSummaries
                            )
                              ? data?.reference?.matterSummaries![0]
                              : {},
                          },
                        })
                      }
                      textDecoration="underline"
                      to={`${pagePaths.EXPENSE_DETAIL}/${expense.expenseId}/${pageKeywords.mode.VIEW}`}
                    >
                      {
                        master.getBy(
                          MasterKey.ExpenseItems,
                          expense.expenseItemId!
                        )?.name
                      }
                    </Link>
                    <HStack
                      justifyContent="space-between"
                      width={{ base: '50%', md: '20%' }}
                    >
                      <Text textAlign="right" width="120px">
                        ￥ {expense.price?.toLocaleString()} -
                      </Text>
                      {/* <Box width="40%">
                    <IconButton
                      aria-label="receipt"
                      hidden={!expense.fileAttached}
                      icon={<FiFileText />}
                      onClick={() =>
                        getAttachedFile.mutate({
                          mode: FileKind.Receipt,
                          id: expense.expenseId,
                        })
                      }
                    />
                  </Box> */}
                    </HStack>
                  </HStack>
                )) || [<Text key="empty">保存された経費はありません</Text>]
            }
          />
        </Skeleton>
        <HStack justifyContent="center" width="100%">
          <AddButton
            onClick={() => {
              actions.updatePageInfo({
                MATTER_EXPENSE: {
                  matterSummary: ListUtils.isNotEmpty(
                    data?.reference?.matterSummaries
                  )
                    ? data?.reference?.matterSummaries![0]
                    : {},
                },
              });
              navigate(
                `${pagePaths.EXPENSE_DETAIL}?date=${dateString}&matterId=${
                  ListUtils.isNotEmpty(data?.reference?.matterSummaries)
                    ? data?.reference?.matterSummaries![0].matterId
                    : undefined
                }&dailyMatterId=${
                  ListUtils.isNotEmpty(data?.reference?.dailyMatterSummaries)
                    ? data?.reference?.dailyMatterSummaries![0].dailyMatterId
                    : undefined
                }`
              );
            }}
          />
        </HStack>
        <BackButton onClick={() => navigate(-1)} />
      </VStack>
    </Wireframe>
  );
};

export default MatterExpense;
